// ============================================================================
//  TotpEnrollment.jsx
//  First-login 2FA screen for the per-agency rollout.
//
//  Consumes the login "gate" response from Account/GetTokenNormal:
//    { twoFactor: "enrollment_required" | "required", preAuthToken, PINID, ... }
//
//  Flow:
//    - The preAuthToken is stored as access_token so the existing axios request
//      interceptor attaches it as the Bearer. It can ONLY reach the AllowPre2fa
//      endpoints (GenerateQR / QRValidate); the default policy blocks it
//      everywhere else, so the user is not "logged in" yet.
//    - enrollment_required: call GenerateQR (identity comes from the token),
//      render the otpauth:// URI as a QR + manual key + app download links,
//      user enters one code, Account/QRValidate confirms and mints the full token.
//    - required (already enrolled): skip the QR, just take the code.
//
//  npm i qrcode.react   (no QR library is currently in package.json)
// ============================================================================
import React, { useEffect, useState, useCallback, useContext } from "react";
// import { QRCodeCanvas } from "qrcode.react";
import GoogleAuthServices from "@/CADServices/APIs/googleAuth";
import { toastifyError } from "@/Components/Common/AlertMsg";
import { LoginContext } from "@/CADContext/loginAuth";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Decrypt_Id_Name } from "../Common/Utility";
import { useDispatch } from "react-redux";
import { insert_LocalStoreData } from "@/redux/actions/Agency";

const APP_LINKS = {
    ios: "https://apps.apple.com/app/google-authenticator/id388497605",
    android: "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2",
};

// Pull the base32 secret out of the otpauth:// URI for manual entry.
const parseSecret = (uri) => {
    if (!uri) return "";
    const m = String(uri).match(/[?&]secret=([^&]+)/i);
    return m ? decodeURIComponent(m[1]) : "";
};

const TotpEnrollment = ({ IsLoggedIn }) => {
    const needsEnrollment = true;
    const { loginAPIData, loginData } = useContext(LoginContext);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [otpAuthUri, setOtpAuthUri] = useState("");
    const [manualKey, setManualKey] = useState("");
    const [code, setCode] = useState("");
    const [uniqueKey, setUniqueKey] = useState("");
    const [loading, setLoading] = useState(needsEnrollment);
    const [verifying, setVerifying] = useState(false);
    // Make the pre-auth token the active Bearer for the enrollment calls.
    useEffect(() => {
        if (loginAPIData?.preAuthToken) {
            sessionStorage.setItem("access_token", loginAPIData.preAuthToken);
        }
    }, [loginAPIData?.preAuthToken]);

    const loadQr = useCallback(async () => {
        setLoading(true);
        const qrPayload = {
            UserName: loginAPIData?.userName,
            UserPINID: loginAPIData?.PINID,
        };
        try {
            // Body is ignored server-side; identity is read from the token claims.
            const res = await GoogleAuthServices.generateQR(qrPayload);
            const uri = res?.data?.QRCode;
            const uniqueKey = res?.data?.UniqueKey;
            if (res?.status === 200 && uri) {
                setOtpAuthUri(uri);
                setManualKey(parseSecret(uri));
                setUniqueKey(uniqueKey);
            } else {
                toastifyError(res?.data?.error_description || "Could not start 2FA setup. Please try again.");
            }
        } catch (err) {
            console.error("GenerateQR failed:", err);
            toastifyError("Could not start 2FA setup. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [loginAPIData?.userName, loginAPIData?.PINID]);

    useEffect(() => {
        if (IsLoggedIn && loginAPIData?.PINID) loadQr();
    }, [IsLoggedIn, loadQr]);

    const uniqueId = sessionStorage.getItem('UniqueUserID') ? Decrypt_Id_Name(sessionStorage.getItem('UniqueUserID'), 'UForUniqueUserID') : '';
    const InsertAccessOrRefreshToken = (data) => {
        const Tokens = {
            'AgencyID': data['AgencyID'],
            'Agency_Name': data['Agency_Name'],
            'PINID': data['PINID'],
            'UserName': data['userName'],
            'SessionTimeOut': data['SessionTimeOut'],
            'ORI': data['ORI'],
            'BaseDate': data['ReportSubmissionDate'],
            'StateCode': data['StateCode'],
            'LoginUserName': data['LoginUserName'],
            'StateName': data['StateName'],
            'fullName': data['fullName'],
            'IsSuperadmin': data['IsSuperadmin'] === "1" ? true : false,
            'IsSupervisor': data['IsSupervisor'],
            'IsIncidentEditable': data['IsIncidentEditable'] === "False" || data['IsIncidentEditable'] === "false" || data['IsIncidentEditable'] === false ? false : true,
            'IsAdministrativeSystem': data['IsAdministrativeSystem'] === "False" || data['IsAdministrativeSystem'] === "false" || data['IsAdministrativeSystem'] === false ? false : true,
            'Is2FAEnabled': data['Is2FAEnabled'] === "False" || data['Is2FAEnabled'] === "false" || data['Is2FAEnabled'] === false ? false : true,
            'NCICLoginId': data['NCICLoginId'],
            'NCICLoginPassword': data['NCICLoginPassword'],
            'NCICLoginTerminalID': data['NCICLoginTerminalID'],
            'NCICORI': data['NCICORI'],
            'ReportApproval': data['ReportApproval'] === "1" ? "Single" : 'Multi',
            'IsLevel': data['IsLevel'],
            'IsCaseManagementVisible': data['IsCaseManagementVisible'] === "False" || data['IsCaseManagementVisible'] === "false" || data['IsCaseManagementVisible'] === false ? false : true,
            'AgencyAddress': data['Agency_Address'],
            'ZipCode': data['ZipCode'],
            'City': data['CityName'],
            'CaseAging': data['CaseAging'],
        }
        const val = {
            UniqueId: uniqueId,
            Key: JSON.stringify(Tokens)
        }
        sessionStorage.setItem('refresh_token', data['refresh_token']);
        sessionStorage.setItem('access_token', data['access_token']);
        sessionStorage.setItem('AgencyID', data['AgencyID']);
        sessionStorage.setItem("SessionTimeOut", data['SessionTimeOut']);
        dispatch(insert_LocalStoreData(val, Tokens));
    }

    const handleVerify = async (e) => {
        e.preventDefault();
        if (!/^\d{6}$/.test(code)) {
            toastifyError("Enter the 6-digit code from your authenticator app.");
            return;
        }
        setVerifying(true);
        try {
            const payload = {
                Pin: code,
                IsFrom2FA: 1,
                username: loginData?.username,
                password: loginData?.password,
                UniqueId: loginData?.UniqueId,
                UnitId: loginData?.UnitId,
                AgencyId: loginData?.AgencyId,
                grant_type: loginData?.grant_type,
                FullUserName: loginAPIData?.userName,
                UserPINID: loginAPIData?.PINID,
                UserUniqueKey: uniqueKey,
            };
            const { data } = await GoogleAuthServices.qRAccountValidate(payload);
            if (data?.error === "200" || data?.access_token) {
                // Parent swaps the pre-auth token for the real token and navigates.
                sessionStorage.setItem('is-login', true);
                InsertAccessOrRefreshToken(data);
                axios.defaults.headers.common['Authorization'] = `Bearer ${data['access_token']}`;
                if (loginData?.typeOfAccount === "RMS") {
                    navigate('/dashboard-page');
                } else if (loginData?.typeOfAccount === "CAD") {
                    navigate('/cad/dashboard-page');
                } else {
                    navigate("/")
                }
            } else {
                toastifyError(data?.message || "Invalid code. Please try again.");
                setCode("");
            }
        } catch (err) {
            console.error("QRValidate failed:", err);
            toastifyError("Invalid code. Please try again.");
            setCode("");
        } finally {
            setVerifying(false);
        }
    };

    return (
        <div className="login-container">
            <div className="auth mobile-login mx-4">
                <div className="card py-3 col-12 col-sm-6 col-md-5 col-lg-3">
                    <div className="card-body">
                        <div className="text-start pb-2">
                            <h5 className="m-0 pb-1">Two-factor authentication</h5>
                            <p className="m-0 text-muted" style={{ fontSize: "13px" }}>
                                Your username and password do not change. This is a one-time setup
                                that adds a second step at sign-in.
                            </p>
                        </div>

                        {needsEnrollment && (
                            <>
                                <ol className="pl-3 mb-2" style={{ fontSize: "13px", textAlign: "left" }}>
                                    <li>
                                        Install an authenticator app (Google Authenticator,
                                        Microsoft Authenticator, Authy, or any other).
                                        <div className="mt-1">
                                            <a href={APP_LINKS.ios} target="_blank" rel="noreferrer" className="mr-2">App Store</a>
                                            <a href={APP_LINKS.android} target="_blank" rel="noreferrer">Google Play</a>
                                        </div>
                                    </li>
                                    <li className="mt-1">Scan the code below.</li>
                                    <li className="mt-1">Enter the 6-digit code the app shows.</li>
                                </ol>

                                <div className="d-flex justify-content-center align-items-center my-2">
                                    {loading ? (
                                        <output className="spinner-border text-success" />
                                    ) : otpAuthUri ? (
                                        // <QRCodeCanvas value={otpAuthUri} size={168} includeMargin />
                                        <img src={otpAuthUri} alt="QR Code" style={{ maxWidth: '200px' }} />
                                    ) : (
                                        <span className="text-danger" style={{ fontSize: "13px" }}>
                                            Setup code unavailable.
                                        </span>
                                    )}
                                </div>

                                {manualKey && (
                                    <div className="text-center mb-2">
                                        <small className="text-muted d-block">Can't scan? Enter this key:</small>
                                        <code style={{ wordBreak: "break-all", fontSize: "13px" }}>{manualKey}</code>
                                    </div>
                                )}
                            </>
                        )}

                        <form onSubmit={handleVerify}>
                            <div className="form-group">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={6}
                                    className="form-control text-center"
                                    placeholder="Enter 6-digit code"
                                    value={code}
                                    onChange={(e) => {
                                        if (/^\d{0,6}$/.test(e.target.value)) setCode(e.target.value);
                                    }}
                                />
                            </div>
                            <div className="mt-2">
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-block"
                                    disabled={verifying || (needsEnrollment && !otpAuthUri) || code.length !== 6}
                                >
                                    {verifying ? "Verifying..." : "Verify and continue"}
                                </button>
                            </div>
                        </form>

                        <p className="text-center text-muted mt-2 mb-0" style={{ fontSize: "12px" }}>
                            Locked out or no phone available? Contact your administrator to reset your 2FA.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TotpEnrollment;
